import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import AllergyWorkupSection from "../../components/AllergyWorkupSection/AllergyWorkupSection";
import BronchoscopySection from "../../components/BronchoscopySection/BronchoscopySection";
import ImagingSection from "../../components/ImagingSection/ImagingSection";
import PlanSection from "../../components/PlanSection/PlanSection";
import PulmonaryFunctionTestsSection from "../../components/PulmonaryFunctionTestsSection/PulmonaryFunctionTestsSection";
import PulmonologySection from "../../components/PulmonologySection/PulmonologySection";
import ReasonAndSymptomsSection from "../../components/ReasonAndSymptomsSection/ReasonAndSymptomsSection";
import SpecificBiologySection from "../../components/SpecificBiologySection/SpecificBiologySection";
import SuspectedDiagnosesSection from "../../components/SuspectedDiagnosesSection/SuspectedDiagnosesSection";
import VitalsAndPhysicalExamSection from "../../components/VitalsAndPhysicalExamSection/VitalsAndPhysicalExamSection";
import usePulmonologyRecords from "../../hooks/usePulmonologyRecords";
import "./PulmonologyConsultationPage.css";

const sections = [
  { id: "reason", titleKey: "page.sections.reason", Component: ReasonAndSymptomsSection },
  { id: "vitals", titleKey: "page.sections.vitals", Component: VitalsAndPhysicalExamSection },
  { id: "diagnoses", titleKey: "page.sections.diagnoses", Component: SuspectedDiagnosesSection },
  { id: "imaging", titleKey: "page.sections.imaging", Component: ImagingSection },
  { id: "pft", titleKey: "page.sections.pft", Component: PulmonaryFunctionTestsSection },
  { id: "bronchoscopy", titleKey: "page.sections.bronchoscopy", Component: BronchoscopySection },
  { id: "biology", titleKey: "page.sections.biology", Component: SpecificBiologySection },
  { id: "allergy", titleKey: "page.sections.allergy", Component: AllergyWorkupSection },
  { id: "plan", titleKey: "page.sections.plan", Component: PlanSection },
];

const resourceBySection = {
  reason: "reason-symptoms",
  vitals: "physical-examination",
  diagnoses: "suspected-diagnoses",
  pft: "pulmonary-function-tests",
  biology: "specific-biology",
  allergy: "allergy-workup",
  plan: "conclusion-plan",
};

export default function PulmonologyConsultationPage({ patientId, onContinueToStep2, onCancelConsultation }) {
  const { t } = useTranslation("pulmonology");
  const { records, loading, saving, error, save } = usePulmonologyRecords(patientId);
  const [openSections, setOpenSections] = useState(["reason"]);
  const [activeSection, setActiveSection] = useState("reason");
  const [completedSections, setCompletedSections] = useState(["reason"]);
  const [touchedSections, setTouchedSections] = useState([]);
  const [step2Ready, setStep2Ready] = useState(false);
  const [notice, setNotice] = useState("");
  const markSectionTouched = (id) => {
    setTouchedSections((current) => current.includes(id) ? current : [...current, id]);
  };

  const toggleSectionComplete = (id) => {
    markSectionTouched(id);
    setCompletedSections((current) => current.includes(id)
      ? current.filter((sectionId) => sectionId !== id)
      : [...current, id]);
  };

  const openById = (id) => {
    markSectionTouched(id);
    setActiveSection(id);
    setOpenSections((current) => current.includes(id) ? current : [...current, id]);
    document.getElementById(`pulmo-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const toggleSection = (id) => {
    markSectionTouched(id);
    setActiveSection(id);
    setOpenSections((current) => current.includes(id)
      ? current.filter((sectionId) => sectionId !== id)
      : [...current, id]);
  };

  const saveSection = (resource, payload, recordId) => save(resource, payload, { id: recordId });

  const handleContinueToStep2 = () => {
    setStep2Ready(true);
    setCompletedSections((current) => current.includes("plan") ? current : [...current, "plan"]);
    if (onContinueToStep2) {
      onContinueToStep2();
      return;
    }
    setNotice(t("page.consultationReady"));
  };

  const totalSections = sections.length + 1;
  const completedCount = completedSections.length + Number(step2Ready);

  const handleCancelConsultation = () => {
    if (onCancelConsultation) {
      onCancelConsultation();
      return;
    }
    setNotice(t("page.consultationCancelled"));
  };

  return (
    <main className="pulmo-page">
      <div className="pulmo-step-nav-shell">
        <nav className="pulmo-step-nav" aria-label={t("page.ariaSections")}>
          {sections.map((section, index) => {
            const complete = completedSections.includes(section.id);
            const touched = touchedSections.includes(section.id);
            return <button type="button" key={section.id} className={`${section.id === activeSection ? "is-active" : ""}${complete ? " is-complete" : touched ? " is-incomplete" : ""}`} onClick={() => openById(section.id)}>
              <span>{complete ? <Check size={11} /> : index + 1}</span>{t(section.titleKey)}
            </button>
          })}
        </nav>
      </div>
      <div className="pulmo-sections">
        {sections.map(({ Component, titleKey, ...section }, index) => (
          <PulmonologySection
            key={section.id}
            section={{ ...section, title: t(titleKey) }}
            index={index}
            open={openSections.includes(section.id)}
            status={completedSections.includes(section.id) ? "complete" : touchedSections.includes(section.id) ? "incomplete" : "untouched"}
            onMarkComplete={toggleSectionComplete}
            onToggle={() => toggleSection(section.id)}
          >
            <Component
              patientId={patientId}
              record={records[resourceBySection[section.id] || section.id]}
              records={records}
              loading={loading}
              saving={saving}
              canSave={Boolean(patientId)}
              saveRecord={(resource, payload, recordId) => saveSection(resource, payload, recordId)}
              onContinue={(documentKey) => documentKey ? onContinueToStep2?.(documentKey) : handleContinueToStep2()}
              onCancel={handleCancelConsultation}
              sectionsComplete={t("page.progressComplete", { count: completedCount, total: totalSections })}
            />
          </PulmonologySection>
        ))}
      </div>
      {notice && <div className="pulmo-notice" role="status">{notice}<button type="button" onClick={() => setNotice("")}>{t("common.dismiss")}</button></div>}
      {error && <div className="pulmo-notice" role="alert">{error}</div>}
    </main>
  );
}
